import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, Flame } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Review() {
  const [params] = useSearchParams();
  const ref = params.get("ref") || "";
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/reviews`, { order_ref: ref, rating, text, name });
      setDone(data.message);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — try again");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#08090d] text-white flex items-center justify-center px-6" data-testid="review-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="card-glow-border rounded-3xl p-10 max-w-md w-full bg-[#0f1117]"
      >
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#f97316] to-[#ea580c] shadow-[0_0_40px_rgba(255,98,31,0.5)] mb-6">
          <Flame size={22} className="text-white" strokeWidth={2.2} />
        </div>
        {done ? (
          <>
            <h1 className="font-display font-bold text-2xl mb-3" data-testid="review-thanks">Thank you.</h1>
            <p className="text-sm text-slate-400 mb-8">{done}</p>
            <Link to="/" className="font-tech text-[11px] tracking-[0.15em] uppercase text-[#f97316] hover:underline" data-testid="review-back">
              Back to NOXWAY →
            </Link>
          </>
        ) : (
          <>
            <h1 className="font-display font-bold text-2xl mb-2">Rate your NOXWAY experience</h1>
            <p className="text-xs text-slate-500 mb-7">Order ref {ref || "—"} · takes less than a minute</p>
            <form onSubmit={submit} className="space-y-5">
              <div className="flex gap-2" data-testid="review-stars">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}
                    aria-label={`${n} stars`}>
                    <Star size={30} className={n <= rating ? "text-[#f97316] fill-[#f97316]" : "text-slate-600"} />
                  </button>
                ))}
              </div>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your first name (shown publicly)"
                data-testid="review-name"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
              <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="How did it go? Application, delivery, peace of mind…" required rows={4}
                data-testid="review-text"
                className="w-full bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm outline-none focus:border-[#f97316] placeholder:text-slate-600" />
              {error && <p className="text-xs text-red-400" data-testid="review-error">{error}</p>}
              <button type="submit" disabled={busy || !ref} data-testid="review-submit"
                className="w-full font-display font-bold tracking-[0.12em] text-sm px-8 py-3.5 rounded-full bg-gradient-to-r from-[#f97316] to-[#ea580c] text-white disabled:opacity-50">
                {busy ? "SENDING…" : "SUBMIT REVIEW"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
